import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Layout, Popconfirm, Space, Spin, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';

const { Content } = Layout;
const { Text, Title } = Typography;

export const CHANNEL_TYPE_LABELS = {
  1: 'SIP',
  2: 'MAX',
  3: 'Виджет',
  4: 'VK',
  5: 'Bitrix24',
  6: 'External',
  7: 'Чат',
};

export const CHANNEL_RUNTIME_STATUS_LABELS = {
  running: 'Запущен',
  stopped: 'Остановлен',
  deactivated: 'Деактивирован',
};

export const CHANNEL_RUNTIME_STATUS_COLORS = {
  running: 'green',
  stopped: 'orange',
  deactivated: 'default',
};

export const getChannelTypeLabel = (vid) => CHANNEL_TYPE_LABELS[vid] || `Канал ${vid}`;
export const getChannelRuntimeStatusLabel = (status) => (
  CHANNEL_RUNTIME_STATUS_LABELS[status] || 'Неизвестно'
);
export const getChannelRuntimeStatusColor = (status) => (
  CHANNEL_RUNTIME_STATUS_COLORS[status] || 'default'
);

export const getChannels = () => serverFetch('channel');

export const getChannel = (id) => serverFetch(`channel/${id}`);

export const createChannel = (data) => serverFetch('channel', {
  method: 'POST',
  bodyData: data,
});

export const updateChannel = (data) => serverFetch('channel', {
  method: 'PUT',
  bodyData: data,
});

export const deleteChannel = (id) => serverFetch(`channel/${id}`, {
  method: 'DELETE',
});

const Channels = () => {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingChannelId, setDeletingChannelId] = useState(null);

  const loadChannels = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const result = await getChannels();
      setChannels(result || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const handleDeleteChannel = async (channelId) => {
    setError('');
    setDeletingChannelId(channelId);
    try {
      await deleteChannel(channelId);
      setChannels((currentChannels) => (
        currentChannels.filter((channel) => channel.id !== channelId)
      ));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingChannelId(null);
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Тип',
      dataIndex: 'vid',
      width: 140,
      render: (vid) => <Tag>{getChannelTypeLabel(vid)}</Tag>,
    },
    {
      title: 'Состояние',
      dataIndex: 'runtimeStatus',
      width: 150,
      render: (runtimeStatus) => (
        <Tag color={getChannelRuntimeStatusColor(runtimeStatus)}>
          {getChannelRuntimeStatusLabel(runtimeStatus)}
        </Tag>
      ),
    },
    {
      title: 'Активен',
      dataIndex: 'active',
      width: 120,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Активен' : 'Отключен'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, channel) => (
        <span className="table-row-action" onClick={(event) => event.stopPropagation()}>
          <Popconfirm
            title="Удалить канал?"
            description="Активный канал будет остановлен."
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true, loading: deletingChannelId === channel.id }}
            onConfirm={() => handleDeleteChannel(channel.id)}
          >
            <Button
              danger
              type="link"
              icon={<DeleteOutlined />}
              loading={deletingChannelId === channel.id}
            >
              Удалить
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <Content className="users-page">
      <div className="users-panel">
        <div className="panel-title users-title">
          <Space direction="vertical" size={0}>
            <Title level={5}>Каналы</Title>
            <Text type="secondary">{channels.length}</Text>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadChannels}>
              Обновить
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/channels/new')}>
              Создать
            </Button>
          </Space>
        </div>

        {error && (
          <Alert className="panel-alert" type="error" message={error} showIcon />
        )}

        <Spin spinning={isLoading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={channels}
            locale={{ emptyText: <Empty description="Каналов пока нет" /> }}
            pagination={false}
            onRow={(channel) => ({
              onClick: () => navigate(`/channels/${channel.id}`),
            })}
          />
        </Spin>
      </div>
    </Content>
  );
};

export default Channels;
